import { avatarUrlFor } from "../apps/favorites.js";
import { getHomeIdentity } from "./launch-oidc.js";
import { readIframeUrl } from "./embed-url.js";

const SPACE_MEMBERS_URL = "https://spacemembers-rqfgqadjxa-ez.a.run.app";
const SUMMON_USER_URL = "https://summonuser-rqfgqadjxa-ez.a.run.app";
const W2L_APP_SECRET = "yOL7/Xl3FsIHGYihibY+thG0MlTeezrqx3pONsvCODQ=";
const AUTH_HEADERS = {
  "Content-Type": "application/json",
  "X-W2L-Secret": W2L_APP_SECRET,
};

const memberCacheBySpace = new Map();

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function memberUserId(member) {
  return String(member && (member.userId || member.id) || "");
}

function memberDisplayName(member) {
  return String((member && (member.nickname || member.firstName)) || "عضو").trim() || "عضو";
}

function memberFirstName(member) {
  const firstName = String((member && member.firstName) || "").trim();
  if (firstName) return firstName;
  return memberDisplayName(member).split(/\s+/)[0] || "";
}

function matchesFirstName(member, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return false;
  return memberFirstName(member).toLowerCase().startsWith(needle);
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: AUTH_HEADERS,
    body: JSON.stringify(body || {}),
  });
  const text = await res.text();
  let data = {};
  try {
    data = JSON.parse(text);
  } catch {
    data = {};
  }
  if (!res.ok) {
    throw new Error(data.error || data.message || `request_failed_${res.status}`);
  }
  return data;
}

async function loadSpaceMembers(spaceId) {
  const key = String(spaceId || "");
  if (!key) return [];
  if (memberCacheBySpace.has(key)) {
    return memberCacheBySpace.get(key);
  }

  const data = await postJson(SPACE_MEMBERS_URL, { spaceId: key });
  const members = Array.isArray(data.members) ? data.members : [];
  const normalized = members
    .map((member) => ({
      ...member,
      userId: memberUserId(member),
      nickname: memberDisplayName(member),
      avatarLink: member && member.avatarLink ? member.avatarLink : null,
    }))
    .filter((member) => member.userId);

  memberCacheBySpace.set(key, normalized);
  return normalized;
}

export function setInvitePanelOpen(root, open) {
  const wrap = root.querySelector("[data-invite-wrap]");
  const panel = root.querySelector("[data-invite-panel]");
  const toggle = root.querySelector('[data-action="invite-toggle"]');
  const shell = root.querySelector(".app-shell");
  if (!wrap || !panel || !toggle) return;

  panel.hidden = !open;
  toggle.setAttribute("aria-expanded", String(open));
  wrap.classList.toggle("is-open", open);
  if (shell) shell.classList.toggle("invite-open", open);
  if (open) {
    const search = panel.querySelector("[data-invite-search]");
    if (search) search.focus();
  }
}

export function setInviteStatus(root, message) {
  const status = root.querySelector("[data-invite-status]");
  if (!status) return;
  status.textContent = message || "";
  status.hidden = !message;
}

function selectedIds(state) {
  return state.selected.map((entry) => entry.userId);
}

function renderSelected(root, state) {
  const selectedEl = root.querySelector("[data-invite-selected]");
  const sendBtn = root.querySelector('[data-action="invite-send"]');
  if (!selectedEl) return;

  if (!state.selected.length) {
    selectedEl.innerHTML = `<p class="invite-empty">أضف أسماء ثم اضغط دعوة</p>`;
  } else {
    selectedEl.innerHTML = state.selected
      .map((entry) => {
        const name = escapeHtml(entry.nickname);
        const avatar = escapeHtml(avatarUrlFor(entry));
        const userId = escapeHtml(entry.userId);
        return `
          <div class="invite-chip">
            <img class="favorited-by-avatar" src="${avatar}" alt="" />
            <span class="favorited-by-name">${name}</span>
            <button type="button" class="invite-chip-remove" data-action="invite-remove" data-user-id="${userId}" aria-label="إزالة ${name}">×</button>
          </div>
        `;
      })
      .join("");
  }

  if (sendBtn) {
    sendBtn.disabled = state.selected.length === 0 || state.sending;
  }
}

function renderResults(root, state) {
  const resultsEl = root.querySelector("[data-invite-results]");
  if (!resultsEl) return;

  const query = state.query.trim();
  if (!query) {
    resultsEl.innerHTML = `<p class="invite-empty">اكتب الاسم الأول للبحث</p>`;
    return;
  }

  if (state.loadingMembers) {
    resultsEl.innerHTML = `<p class="invite-empty">جاري التحميل…</p>`;
    return;
  }

  const taken = new Set(selectedIds(state));
  const matches = state.members.filter((member) => {
    if (member.userId === state.currentUserId) return false;
    if (taken.has(member.userId)) return false;
    return matchesFirstName(member, query);
  });

  if (!matches.length) {
    resultsEl.innerHTML = `<p class="invite-empty">لا يوجد مطابقون</p>`;
    return;
  }

  resultsEl.innerHTML = matches
    .slice(0, 12)
    .map((member) => {
      const name = escapeHtml(member.nickname);
      const avatar = escapeHtml(avatarUrlFor(member));
      const userId = escapeHtml(member.userId);
      return `
        <button type="button" class="favorited-by-user" data-action="invite-add" data-user-id="${userId}">
          <img class="favorited-by-avatar" src="${avatar}" alt="" />
          <span class="favorited-by-name">${name}</span>
        </button>
      `;
    })
    .join("");
}

function refreshPanel(root, state) {
  renderResults(root, state);
  renderSelected(root, state);
}

async function ensureMembers(state) {
  if (state.members.length || state.loadingMembers) return;
  state.loadingMembers = true;
  try {
    state.members = await loadSpaceMembers(state.spaceId);
  } finally {
    state.loadingMembers = false;
  }
}

async function inviteSelected(root, app, state) {
  if (!state.selected.length || state.sending) return;

  const identity = getHomeIdentity();
  if (!identity.userId || !identity.spaceId) {
    setInviteStatus(root, "تعذر إرسال الدعوة: هوية المستخدم غير متوفرة");
    return;
  }

  state.sending = true;
  refreshPanel(root, state);
  setInviteStatus(root, "جاري إرسال الدعوة…");

  let successCount = 0;
  let lastError = "";

  for (const target of state.selected) {
    try {
      const data = await postJson(SUMMON_USER_URL, {
        targetUserId: target.userId,
        targetName: target.nickname || "عضو",
        fromName: identity.nickname || "عضو",
        senderUserId: identity.userId,
        spaceId: identity.spaceId,
        currentMapId: identity.mapId || "",
        appId: app.id,
        appName: app.name || app.id,
        appUrl: app.url || "",
        embedUrl: readIframeUrl(root, app),
      });
      if (data && data.success === true) {
        successCount += 1;
      } else {
        lastError = data.error || data.message || "invite_failed";
      }
    } catch (error) {
      lastError = error && error.message ? String(error.message) : "invite_failed";
    }
  }

  state.sending = false;
  refreshPanel(root, state);

  if (successCount === state.selected.length) {
    setInviteStatus(
      root,
      successCount === 1
        ? `تم إرسال الدعوة إلى ${state.selected[0].nickname}`
        : `تم إرسال ${successCount} دعوة`,
    );
    state.selected = [];
    refreshPanel(root, state);
    return;
  }

  if (successCount > 0) {
    setInviteStatus(root, `تم إرسال ${successCount} من أصل ${state.selected.length}`);
    return;
  }

  setInviteStatus(root, lastError === "invite_failed" ? "تعذر إرسال الدعوة" : lastError || "تعذر إرسال الدعوة");
}

export function bindAppInvite(root, app) {
  const wrap = root.querySelector("[data-invite-wrap]");
  if (!wrap || !app) return;

  const identity = getHomeIdentity();
  const state = {
    spaceId: identity.spaceId,
    currentUserId: String(identity.userId || ""),
    members: [],
    selected: [],
    query: "",
    loadingMembers: false,
    sending: false,
    searchTimer: 0,
  };

  refreshPanel(root, state);

  const toggle = root.querySelector('[data-action="invite-toggle"]');
  if (toggle) {
    toggle.addEventListener("click", async (event) => {
      event.preventDefault();
      event.stopPropagation();
      const panel = root.querySelector("[data-invite-panel]");
      if (!panel) return;
      const opening = panel.hidden;
      setInvitePanelOpen(root, opening);
      if (opening) {
        setInviteStatus(root, "");
        await ensureMembers(state);
        refreshPanel(root, state);
      }
    });
  }

  const search = root.querySelector("[data-invite-search]");
  if (search) {
    search.addEventListener("click", (event) => {
      event.stopPropagation();
    });
    search.addEventListener("pointerdown", (event) => {
      event.stopPropagation();
    });
    search.addEventListener("input", () => {
      state.query = search.value || "";
      window.clearTimeout(state.searchTimer);
      state.searchTimer = window.setTimeout(() => refreshPanel(root, state), 150);
    });
  }

  wrap.addEventListener("pointerdown", (event) => {
    event.stopPropagation();
  });
  wrap.addEventListener("click", (event) => {
    event.stopPropagation();
  });

  const panel = root.querySelector("[data-invite-panel]");
  if (panel) {
    panel.addEventListener("click", async (event) => {
      const addBtn = event.target.closest('[data-action="invite-add"]');
      if (addBtn) {
        event.preventDefault();
        event.stopPropagation();
        const userId = addBtn.getAttribute("data-user-id");
        const member = state.members.find((entry) => entry.userId === userId);
        if (member && !selectedIds(state).includes(member.userId)) {
          state.selected.push(member);
          refreshPanel(root, state);
        }
        return;
      }

      const removeBtn = event.target.closest('[data-action="invite-remove"]');
      if (removeBtn) {
        event.preventDefault();
        event.stopPropagation();
        const userId = removeBtn.getAttribute("data-user-id");
        state.selected = state.selected.filter((entry) => entry.userId !== userId);
        refreshPanel(root, state);
        return;
      }

      const sendBtn = event.target.closest('[data-action="invite-send"]');
      if (sendBtn) {
        event.preventDefault();
        event.stopPropagation();
        await inviteSelected(root, app, state);
      }
    });
  }

  if (root._inviteOutsideClose) {
    document.removeEventListener("pointerdown", root._inviteOutsideClose);
  }
  root._inviteOutsideClose = (event) => {
    const path = typeof event.composedPath === "function" ? event.composedPath() : [];
    if (path.includes(wrap)) return;
    const target = event.target;
    if (target && wrap.contains(target)) return;
    if (target && target.isConnected === false) return;
    setInvitePanelOpen(root, false);
  };
  document.addEventListener("pointerdown", root._inviteOutsideClose);
}
